import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  WritableSignal,
  computed,
  forwardRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { Role } from '@app/utils/interfaces/staff.interface';
import { DropdownOverlayComponent } from '../dropdown-overlay/dropdown-overlay.component';
import { OVERLAY_DATA } from '../overlay.constants';

const noop = (): void => undefined;

@Component({
  selector: 'app-dropdown-field',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './dropdown-field.component.html',
  styleUrl: './dropdown-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownFieldComponent),
      multi: true,
    },
  ],
})
export class DropdownFieldComponent implements ControlValueAccessor {
  public readonly label = input.required<string>();
  public readonly roles = input.required<Role[]>();

  public readonly placeholder = input<string>('');
  public readonly controlId = input<string>('dropdown');
  public readonly emptyMessage = input<string>('This field is required.');
  public readonly invalidMessage = input<string>('Invalid value.');

  protected readonly IconName = IconName;

  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  private readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 8,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -8,
    },
  ];

  private overlayRef: OverlayRef | null = null;

  protected readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('dropdownInput');

  protected readonly value = signal('');
  protected readonly touched = signal(false);
  protected readonly disabled = signal(false);
  protected readonly dropdownOpen = signal(false);
  protected readonly filteredRoles: WritableSignal<Role[]> = signal([]);

  protected readonly isInvalid = computed(() => {
    return this.touched() && !this.dropdownOpen() && this.isValueInvalid();
  });

  protected readonly errorMessage = computed(() => {
    if (!this.isInvalid()) {
      return '';
    }

    return this.value().trim() ? this.invalidMessage() : this.emptyMessage();
  });

  private onChange: (value: string) => void = noop;
  private onTouched: () => void = noop;

  public writeValue(value: string | null): void {
    const nextValue = value ?? '';

    this.value.set(nextValue);
    this.filterRoles(nextValue);
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);

    if (isDisabled) {
      this.closeOverlay();
    }
  }

  protected onInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;

    this.value.set(inputValue);
    this.onChange(inputValue);
    this.filterRoles(inputValue);

    if (this.filteredRoles().length === 0) {
      this.closeOverlay();
    } else if (!this.dropdownOpen()) {
      this.openOverlay();
    }
  }

  protected onFocus(): void {
    if (this.disabled()) {
      return;
    }

    this.filterRoles(this.value());
    this.openOverlay();
  }

  protected onBlur(): void {
    this.markAsTouched();

    setTimeout(() => this.closeOverlay());
  }

  private selectRole(role: Role): void {
    this.value.set(role.name);
    this.onChange(role.name);
    this.filterRoles(role.name);
    this.markAsTouched();
    this.closeOverlay();
  }

  private markAsTouched(): void {
    if (this.touched()) {
      return;
    }

    this.touched.set(true);
    this.onTouched();
  }

  private filterRoles(inputValue: string): void {
    const searchValue = inputValue.trim().toLowerCase();

    this.filteredRoles.set(
      this.roles().filter((role) => role.name.toLowerCase().includes(searchValue)),
    );
  }

  private isValueInvalid(): boolean {
    const currentValue = this.value().trim().toLowerCase();

    if (!currentValue) {
      return true;
    }

    return !this.roles().some((role) => role.name.toLowerCase() === currentValue);
  }

  private openOverlay(): void {
    this.closeOverlay();

    const inputElement = this.inputRef()?.nativeElement;

    if (!inputElement) {
      return;
    }

    const itemHeight = 40; 
    const maxVisibleItems = 4;

    this.overlayRef = this.overlay.create({
      width: inputElement.offsetWidth,
      maxHeight: itemHeight * maxVisibleItems, 
      panelClass: 'dropdown-scrollable-panel',
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(inputElement)
        .withPositions(this.overlayPositions),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const overlayData = {
      roles: this.filteredRoles,
      selectedValue: this.value(),
      onSelect: (role: Role) => this.selectRole(role),
    };

    const customInjector = Injector.create({
      parent: this.injector,
      providers: [{ provide: OVERLAY_DATA, useValue: overlayData }],
    });

    this.dropdownOpen.set(true);

    this.overlayRef
      .detachments()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dropdownOpen.set(false));

    this.overlayRef.attach(new ComponentPortal(DropdownOverlayComponent, null, customInjector));
  }

  private closeOverlay(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.dropdownOpen.set(false);
  }
}
